import React, { useCallback, useRef, useState } from 'react';
import styled from 'astroturf/react';

import { currentUser } from 'js/utils/user';
import { fetchCoauthorOrTitleSuggestions } from './api/TypeaheadApi';
import { Icon } from 'js/design_system/Icon';
import { Typeahead } from 'js/design_system/forms/Typeahead';
import { SearchType } from './KiiTypeahead';

const OptionContainer = styled.div`
  display: flex;
  padding: var(
      --sutd-page-paper-typeahead-paper-suggestion-padding-top-bottom,
      8px
    )
    var(--spacing-xs-8, 8px);
  align-items: flex-start;
  gap: var(--spacing-xs-4, 4px);
  flex: 1 0 0;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;
`;

const PrimaryText = styled.div`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  align-self: stretch;
  color: var(--text-primary, #141413);
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: visible;
`;

const SecondaryText = styled.div`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  color: var(--text-secondary, #535366);
  display: flex;
  align-items: flex-start;
  gap: 8px;
  overflow: visible;
  line-height: 16px;
`;

const ValueContainer = styled.div`
  display: flex;
  padding: var(--spacing-xs-8, 8px);
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  flex: 1 0 0;
  border-radius: var(--border-radius-radius-md);
  background: var(--background-light-blue, #eef2f9);
`;

const SelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid var(--stroke-medium, #535366);
  background: var(--background-white, #fff);
`;

const SelectionInnerContainer = styled.div`
  display: flex;
  padding: var(--spacing-xs-16, 8px);
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
`;

const BoldSpan = styled.span`
  font-weight: bold;
`;

const StyledParagraph = styled.p`
  margin: 0;
`;

const TitleRow = styled.div`
  display: flex;
  gap: var(--spacing-xs-4, 4px);
`;

const StyledIcon = styled(Icon)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const PaddedIcon = styled(Icon)`
  padding-top: 4px;
`;

const AuthorPaddedIcon = styled(Icon)`
  padding-right: 8px;
`;

const AuthorTitleRow = styled.div`
  display: flex;
  flex-direction: column;
`;

type AuthorOption = {
  author_id: number;
  display_name: string;
};

type WorkOption = {
  id: string;
  label: string;
};

type WorkAttributes = {
  title: string | null;
  authors: AuthorOption[] | null;
};

type AuthorAttributes = {
  institution: string | null;
  works: WorkOption[] | null;
};

export type Option = {
  id: number | null;
  label: string;
  inferred_author_id: string | null;
} & WorkAttributes &
  AuthorAttributes;

const BLANK_OPTION = {
  id: null,
  label: '',
  title: null,
  institution: null,
  inferred_author_id: null,
  authors: null,
  works: null,
};

type InputBoxProps = {
  heading?: string;
  placeholderText?: string;
  emptyErrorText: string;
  searchType: SearchType;
  height: number;
  dropdownHeightOffset?: number;
  onChange?: (option: Option) => void;
  onSelect?: (option: Option) => void;
  error: boolean;
  maxHeight?: number;
};

export const SearchBox = ({
  heading,
  placeholderText,
  emptyErrorText,
  searchType,
  height,
  dropdownHeightOffset = 100,
  onChange,
  onSelect,
  error,
}: InputBoxProps) => {
  const [selectedOption, setSelectedOption] = useState<Option>(BLANK_OPTION);
  const [currentSelection, setCurrentSelection] = useState<Option | null>(null);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('');
  // todo cleanup
  const [_, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [previousOption, setPreviousOption] = useState<Option>(BLANK_OPTION);

  const renderValue = useCallback((value?: Option) => {
    return value?.label || '';
  }, []);

  const onSearch = useCallback(
    (searchTerm: string) => {
      return doSearch(searchTerm, searchType);
    },
    [searchType],
  );

  const handleSelect = (option: Option) => {
    setPreviousOption(selectedOption);
    setSelectedOption(option);
    setCurrentSelection(option);
    if (onChange !== undefined) {
      onChange(option);
    }

    if (onSelect !== undefined) {
      onSelect(option);
    }
  };

  const handleChange = (option: Option) => {
    setPreviousOption(selectedOption);
    setSelectedOption(option);
    if (onChange !== undefined) {
      onChange(option);
    }
  };

  const resetOption = () => {
    setSelectedOption(previousOption);
    setCurrentSelection(null);

    if (onChange !== undefined) {
      onChange(previousOption);
    }
  };

  const doSearch = async (term: string, searchType: SearchType) => {
    if (!term || term.length < 3) {
      setCurrentSearchTerm(term);

      return [];
    }

    const displayName = currentUser?.display_name ?? '';

    setLoading(true);

    try {
      const response = await fetchCoauthorOrTitleSuggestions(
        term,
        searchType,
        displayName,
      );

      const data = await response.json();

      setCurrentSearchTerm(term);

      if (Array.isArray(data.knowledge_graph)) {
        return data.knowledge_graph;
      } else {
        return [];
      }
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  };

  const renderPaperTitle = (value: Option) => {
    return (
      <OptionContainer>
        <StyledIcon name={'article'} size={24} />
        <ContentContainer>
          <PrimaryText>{formatValueText(value.label)}</PrimaryText>
          <SecondaryText>
            {formatAuthorNames(value.authors || [])}
          </SecondaryText>
        </ContentContainer>
      </OptionContainer>
    );
  };

  const renderAuthor = (value: Option) => {
    return (
      <OptionContainer style={{ flexDirection: 'column' }}>
        <PrimaryText>
          <AuthorPaddedIcon name={'person'} size={16} />
          {value.label}
        </PrimaryText>
        <AuthorTitleRow>
          {!!value.institution && (
            <SecondaryText style={{ paddingBottom: '5px' }}>
              <AuthorPaddedIcon name={'school'} size={16} />
              {value.institution}
            </SecondaryText>
          )}
          {value.works && (
            <SecondaryText>
              <AuthorPaddedIcon name={'article'} size={16} />
              {value.works?.map((work) => work.label).join(', ')}
            </SecondaryText>
          )}
        </AuthorTitleRow>
      </OptionContainer>
    );
  };

  const renderCoauthor = (value: Option) => {
    return (
      <OptionContainer>
        <PrimaryText>{value.label}</PrimaryText>
        <TitleRow>
          <PaddedIcon name={'article'} size={16} />
          <SecondaryText>{value.title}</SecondaryText>
        </TitleRow>
      </OptionContainer>
    );
  };

  const renderItem = (value: Option) => {
    if (
      searchType === SearchType.PaperTitleAll ||
      searchType === SearchType.PaperTitleConstrained
    ) {
      return renderPaperTitle(value);
    } else if (searchType === SearchType.AuthorClaiming) {
      return renderAuthor(value);
    } else {
      return renderCoauthor(value);
    }
  };

  const formatAuthorNames = (authors: AuthorOption[]) => {
    return `by ${authors.map((author) => author.display_name).join(', ')}`;
  };

  const formatValueText = useCallback(
    (text: string) => {
      const searchTerm = currentSearchTerm;
      const searchWords = searchTerm.trim().split(/\s+/);
      let remainingText = text;
      const elements = [];
      const boldCount: Record<string, number> = {}; // Track bold count for each word's occurrences

      // Initialize bold count based on search term word frequency
      searchWords.forEach((word) => {
        const lowerWord = word.toLowerCase();
        boldCount[lowerWord] = (boldCount[lowerWord] || 0) + 1;
      });

      // Continue searching while there are words with remaining bold count
      while (
        remainingText.length > 0 &&
        Object.values(boldCount).some((count) => count > 0)
      ) {
        // Find the earliest match for any word in `boldCount` that still has remaining occurrences
        let minIndex = remainingText.length;
        let matchedWord = '';
        let beforeText = '';
        let matchedText = '';

        // Check each word and find the first occurrence across remainingText
        Object.keys(boldCount).forEach((word) => {
          if (boldCount[word] > 0) {
            // Only consider words with remaining bold occurrences
            const index = remainingText.toLowerCase().indexOf(word);

            // Update if this occurrence is earlier in the text than previously found
            if (index !== -1 && index < minIndex) {
              minIndex = index;
              matchedWord = word;
              beforeText = remainingText.slice(0, index);
              matchedText = remainingText.slice(index, index + word.length);
            }
          }
        });

        // If we found a word to bold, update elements and remainingText
        if (matchedWord) {
          elements.push(beforeText);
          elements.push(
            <BoldSpan key={`${matchedWord}-${minIndex}`}>
              {matchedText}
            </BoldSpan>,
          );

          // Decrement bold count for the matched word
          boldCount[matchedWord] -= 1;

          // Update remainingText to exclude the bolded match
          remainingText = remainingText.slice(minIndex + matchedWord.length);
        } else {
          // No more matches found; break loop
          break;
        }
      }

      // Append any remaining text that was not matched
      if (remainingText) {
        elements.push(remainingText);
      }

      return <StyledParagraph>{elements}</StyledParagraph>;
    },
    [currentSearchTerm],
  );

  if (currentSelection) {
    return (
      <SelectionContainer>
        <SelectionInnerContainer>
          <ValueContainer>
            <StyledIcon
              name={
                searchType === SearchType.AuthorClaiming ? 'person' : 'article'
              }
              size={24}
            />
            <ContentContainer>
              <PrimaryText>{currentSelection.label}</PrimaryText>
              {currentSelection.authors && (
                <SecondaryText>
                  {formatAuthorNames(currentSelection.authors || [])}
                </SecondaryText>
              )}
              {currentSelection.institution && (
                <SecondaryText>{currentSelection.institution}</SecondaryText>
              )}
            </ContentContainer>
            <CloseButton aria-label="Close" onClick={resetOption}>
              <Icon name={'close'} size={20} />
            </CloseButton>
          </ValueContainer>
        </SelectionInnerContainer>
      </SelectionContainer>
    );
  }

  return (
    <div ref={containerRef} style={{ height: 'inherit' }}>
      <Typeahead<Option>
        value={selectedOption || undefined}
        placeholder={placeholderText ?? ''}
        label={heading}
        hasError={error}
        errors={[emptyErrorText]}
        renderOption={renderItem}
        renderValue={renderValue}
        seamlessLoading={false}
        hideEmpty={true}
        debounceDelay={500}
        onSearch={onSearch}
        onSelect={handleSelect}
        onInputChange={(input) => {
          const selection = {
            id: null,
            label: input,
            title: null,
            authors: null,
            institution: null,
            inferred_author_id: null,
            works: null,
          };
          handleChange(selection);
        }}
        updateSearchTextOnSelect={true}
        alwaysShowScrollbar={true}
        dropdownContainerStyle={{
          padding: '10px 16px 8px 16px',
          overflowY: 'auto',
        }}
        dropdownItemStyle={{ padding: '0' }}
        renderLoading={() => (
          <div className="ds2-5-typeahead--message">Searching...</div>
        )}
        popoverProps={{
          containerStyle: { marginTop: '-5px' },
          maxHeight:
            height > dropdownHeightOffset
              ? height - dropdownHeightOffset
              : height,
        }}
      />
    </div>
  );
};
